import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VstpSettings } from "./VstpSettings";
import FachInfo from "@/components/ui/fach-info";

function TaxSettings() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [countryInfo, setCountryInfo] = useState(null);
  const [originalCountryInfo, setOriginalCountryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state for trade tax
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    trade_tax_liable: false,
    trade_tax_rate: 3.5,
  });
  
  // Error state for church tax validation
  const [churchTaxError, setChurchTaxError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch country info
        const countryResponse = await api.get('/country/info');
        setCountryInfo(countryResponse.data);
        setOriginalCountryInfo(JSON.parse(JSON.stringify(countryResponse.data)));
        
        // Fetch settings for trade tax
        const settingsResponse = await api.get('/settings');
        setSettings(settingsResponse.data);
        setFormData({
          trade_tax_liable: settingsResponse.data.trade_tax_liable || false,
          trade_tax_rate: settingsResponse.data.trade_tax_rate || 3.5,
        });
      } catch (error) {
        console.error("Error fetching tax settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleKleinunternehmerToggle = async () => {
    // Save current scroll position
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    try {
      const newValue = !user?.is_kleinunternehmer;
      await api.put('/user/kleinunternehmer', { 
        is_kleinunternehmer: newValue,
        kleinunternehmer_country: user?.country || 'DE'
      });
      
      // Update user context directly instead of page reload
      await refreshUser();
      
      // Restore scroll position after a brief delay to allow for re-render
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
      
      // Show success message
      toast.success(newValue ? 'Kleinunternehmerregelung aktiviert' : 'Kleinunternehmerregelung deaktiviert');
    } catch (error) {
      console.error('Error updating Kleinunternehmer status:', error);
      toast.error('Fehler beim Aktualisieren');
      
      // Restore scroll position even on error
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  };

  const handleTradeTaxToggle = async () => {
    // Save current scroll position
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    try {
      const newValue = !formData.trade_tax_liable;
      const updatedFormData = { ...formData, trade_tax_liable: newValue };
      
      await api.put('/settings', updatedFormData);
      setFormData(updatedFormData);
      
      // Restore scroll position after a brief delay
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
      
      toast.success(newValue ? 'Gewerbesteuer aktiviert' : 'Gewerbesteuer deaktiviert');
    } catch (error) {
      console.error('Error updating trade tax status:', error);
      toast.error('Fehler beim Aktualisieren der Gewerbesteuer');
      
      // Restore scroll position even on error
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  };

  const handleTradeTaxRateChange = async (newRate) => {
    try {
      const updatedFormData = { ...formData, trade_tax_rate: newRate };
      await api.put('/settings', updatedFormData);
      setFormData(updatedFormData);
      toast.success('Gewerbesteuer-Hebesatz aktualisiert');
    } catch (error) {
      console.error('Error updating trade tax rate:', error);
      toast.error('Fehler beim Aktualisieren des Hebesatzes');
    }
  };

  const handleChurchTaxToggle = async () => {
    // Save current scroll position
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    try {
      const newValue = !countryInfo?.church_tax;
      
      // Clear any previous error
      setChurchTaxError('');
      
      // Validation: If activating church tax, Bundesland must be selected
      if (newValue && !countryInfo?.bundesland) {
        setChurchTaxError('Bitte wählen Sie zuerst ein Bundesland aus, bevor Sie die Kirchensteuer aktivieren.');
        toast.error('Bitte wählen Sie zuerst ein Bundesland aus, bevor Sie die Kirchensteuer aktivieren.');
        return;
      }
      
      const params = { 
        country_code: user?.country || 'DE',
        church_tax: newValue,
        bundesland: newValue ? countryInfo?.bundesland : null
      };
      
      await api.put('/country/update', null, { params });
      
      // Update local state
      const updatedCountryInfo = { ...countryInfo, church_tax: newValue };
      if (!newValue) {
        updatedCountryInfo.bundesland = null;
      }
      setCountryInfo(updatedCountryInfo);
      
      // Restore scroll position after a brief delay
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
      
      toast.success(newValue ? 'Kirchensteuer aktiviert' : 'Kirchensteuer deaktiviert');
    } catch (error) {
      console.error('Error updating church tax status:', error);
      toast.error('Fehler beim Aktualisieren der Kirchensteuer');
      
      // Restore scroll position even on error
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  };

  const handleBundeslandChange = async (bundesland) => {
    try {
      const params = { 
        country_code: user?.country || 'DE',
        church_tax: countryInfo?.church_tax || false,
        bundesland: bundesland
      };
      
      await api.put('/country/update', null, { params });
      
      // Update local state
      setCountryInfo({ ...countryInfo, bundesland });
      
      // Clear church tax error when Bundesland is selected
      if (bundesland && churchTaxError) {
        setChurchTaxError('');
      }
      
      toast.success('Bundesland aktualisiert');
    } catch (error) {
      console.error('Error updating bundesland:', error);
      toast.error('Fehler beim Aktualisieren des Bundeslandes');
    }
  };

  if (loading) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Steuereinstellungen</CardTitle>
        <CardDescription>
          Zentrale Konfiguration für die automatische Steuerberechnung
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Country Display – read-only, Änderung unter "Länderinformationen" */}
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-lg">🌍</span>
              <div>
                <h4 className="font-medium text-gray-900">Steuerland</h4>
                <p className="text-sm text-gray-600">
                  {user?.country === 'DE' && '🇩🇪 Deutschland'}
                  {user?.country === 'AT' && '🇦🇹 Österreich'}
                  {user?.country === 'CH' && '🇨🇭 Schweiz'}
                  {(!user?.country || user?.country === 'OTHER') && '🌍 Anderes Land'}
                </p>
              </div>
            </div>
            <span className="text-xs text-gray-400 italic">Änderbar unter „Länderinformationen" ↓</span>
          </div>
        </div>
        
        {/* 1. Kleinunternehmerregelung - Always visible */}
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <span className="text-lg">📋</span>
              <div>
                <h4 className="font-medium text-gray-900">
                  Kleinunternehmerregelung
                  <FachInfo
                    title="Kleinunternehmerregelung"
                    testId="fach-info-kleinunternehmer"
                    law={user?.country === 'AT' ? '§ 6 Abs. 1 Z 27 UStG (AT)' : user?.country === 'CH' ? 'Art. 10 MWSTG (CH)' : '§ 19 UStG (DE)'}
                  >
                    Unter der Umsatzgrenze musst du keine USt ausweisen und keine Voranmeldung
                    abgeben — darfst im Gegenzug aber auch keine Vorsteuer ziehen. Lohnt sich
                    meist bei Privatkunden-Geschäft, nachteilig bei hohen Eingangsrechnungen.
                  </FachInfo>
                </h4>
                <p className="text-sm text-gray-600">
                  {user?.country === 'DE' && 'Umsatz ≤ 22.000 € (Vorjahr) und ≤ 50.000 € (laufend)'}
                  {user?.country === 'AT' && 'Jahresumsatz ≤ 35.000 €'}
                  {user?.country === 'CH' && 'Jahresumsatz < 100.000 CHF'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`text-sm font-semibold ${user?.is_kleinunternehmer ? 'text-green-600' : 'text-gray-500'}`}>
                {user?.is_kleinunternehmer ? 'Aktiv' : 'Inaktiv'}
              </span>
              <button
                type="button"
                onClick={handleKleinunternehmerToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  user?.is_kleinunternehmer ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    user?.is_kleinunternehmer ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          
          {/* Accordion content for Kleinunternehmer */}
          {user?.is_kleinunternehmer && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="space-y-3">
                {user?.country === 'DE' && (
                  <>
                    <p className="font-semibold text-blue-900">Kleinunternehmerregelung (§ 19 UStG)</p>
                    <p className="text-sm text-blue-800">
                      Als Kleinunternehmer müssen Sie keine Umsatzsteuer auf Ihren Rechnungen ausweisen.
                    </p>
                    <div className="bg-white p-3 rounded border border-blue-300">
                      <p className="font-semibold mb-1 text-sm">Voraussetzungen:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Umsatz im Vorjahr ≤ 22.000 €</li>
                        <li>Voraussichtlicher Umsatz im laufenden Jahr ≤ 50.000 €</li>
                      </ul>
                    </div>
                    <p className="text-blue-700 font-semibold text-sm">
                      ℹ️ Hinweis: Sie können dann auch keine Vorsteuer geltend machen!
                    </p>
                  </>
                )}
                {user?.country === 'AT' && (
                  <>
                    <p className="font-semibold text-blue-900">Kleinunternehmerregelung (§ 6 Abs. 1 Z 27 UStG)</p>
                    <p className="text-sm text-blue-800">
                      Als Kleinunternehmer müssen Sie keine Umsatzsteuer auf Ihren Rechnungen ausweisen.
                    </p>
                    <div className="bg-white p-3 rounded border border-blue-300">
                      <p className="font-semibold mb-1 text-sm">Voraussetzungen:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Jahresumsatz ≤ 35.000 €</li>
                      </ul>
                    </div>
                    <p className="text-blue-700 font-semibold text-sm">
                      ℹ️ Hinweis: Sie können dann auch keine Vorsteuer geltend machen!
                    </p>
                  </>
                )}
                {user?.country === 'CH' && (
                  <>
                    <p className="font-semibold text-blue-900">Kleinunternehmerregelung (Art. 10 Abs. 2 MWSTG)</p>
                    <p className="text-sm text-blue-800">
                      In der Schweiz sind Sie von der MWST-Pflicht befreit, wenn Ihr Jahresumsatz unter CHF 100&apos;000 liegt.
                    </p>
                    <div className="bg-white p-3 rounded border border-blue-300">
                      <p className="font-semibold mb-1 text-sm">Voraussetzungen:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Jahresumsatz {'<'} 100&apos;000 CHF</li>
                      </ul>
                    </div>
                    <p className="text-orange-600 font-semibold text-sm">
                      ⚠️ Achtung: Unterschiedliche Regelung in der Schweiz - bitte Steuerberater konsultieren!
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Vorsteuerpauschalierung – nur AT */}
        <VstpSettings user={user} />

        {/* 2. Gewerbesteuer - Only for DE users */}
        {user?.country === 'DE' && (
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className="text-lg">🏢</span>
                <div>
                  <h4 className="font-medium text-gray-900">Gewerbesteuer (DE)</h4>
                  <p className="text-sm text-gray-600">
                    Nur für Gewerbetreibende (nicht für Freiberufler)
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-sm font-semibold ${formData.trade_tax_liable ? 'text-green-600' : 'text-gray-500'}`}>
                  {formData.trade_tax_liable ? 'Aktiv' : 'Inaktiv'}
                </span>
                <button
                  type="button"
                  onClick={handleTradeTaxToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                    formData.trade_tax_liable ? 'bg-emerald-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.trade_tax_liable ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {/* Accordion content for Gewerbesteuer */}
            {formData.trade_tax_liable && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="space-y-4">
                  <p className="text-sm text-emerald-800">
                    Bei freiberuflicher Tätigkeit (z.B. Beratung, Design, IT) fällt i.d.R. keine Gewerbesteuer an. 
                    Gewerbetreibende (z.B. Handel, Handwerk) sind gewerbesteuerpflichtig.
                  </p>
                  
                  <div>
                    <Label htmlFor="trade_tax_rate">Gewerbesteuer-Hebesatz (%)</Label>
                    <Input
                      id="trade_tax_rate"
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="10"
                      value={formData.trade_tax_rate}
                      onChange={(e) => {
                        const newRate = parseFloat(e.target.value) || 3.5;
                        setFormData({ ...formData, trade_tax_rate: newRate });
                      }}
                      onBlur={(e) => {
                        const newRate = parseFloat(e.target.value) || 3.5;
                        handleTradeTaxRateChange(newRate);
                      }}
                      placeholder="3.5"
                      className="mt-1"
                    />
                    <p className="text-xs text-emerald-600 mt-1">
                      Standard: 3.5% (entspricht 350% kommunaler Hebesatz). Erkundigen Sie sich bei Ihrer Gemeinde nach dem aktuellen Hebesatz.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 3. Kirchensteuer - Only for DE users */}
        {user?.country === 'DE' && (
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className="text-lg">⛪</span>
                <div>
                  <h4 className="font-medium text-gray-900">Kirchensteuer (DE)</h4>
                  <p className="text-sm text-gray-600">
                    8% (BW, BY) oder 9% (andere Bundesländer) der Einkommensteuer
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-sm font-semibold ${countryInfo?.church_tax ? 'text-green-600' : 'text-gray-500'}`}>
                  {countryInfo?.church_tax ? 'Aktiv' : 'Inaktiv'}
                </span>
                <button
                  type="button"
                  onClick={handleChurchTaxToggle}
                  disabled={!countryInfo?.church_tax && !countryInfo?.bundesland}
                  title={!countryInfo?.bundesland && !countryInfo?.church_tax ? 'Bitte zuerst Bundesland auswählen' : undefined}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    countryInfo?.church_tax ? 'bg-purple-600' : 
                    !countryInfo?.bundesland ? 'bg-gray-100 cursor-not-allowed opacity-50' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      countryInfo?.church_tax ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {/* Bundesland selector – always visible for DE users (required before activating Kirchensteuer) */}
            <div className={`mt-4 p-4 rounded-xl border ${countryInfo?.church_tax ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bundesland
                    {!countryInfo?.church_tax && (
                      <span className="ml-2 text-xs text-gray-500 font-normal">
                        (wird für Kirchensteuer benötigt)
                      </span>
                    )}
                  </label>
                  <select
                    value={countryInfo?.bundesland || ''}
                    onChange={(e) => handleBundeslandChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      countryInfo?.church_tax ? 'border-purple-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Bitte auswählen...</option>
                    <optgroup label="8% Kirchensteuer">
                      <option value="BW">🇩🇪 Baden-Württemberg (8%)</option>
                      <option value="BY">🇩🇪 Bayern (8%)</option>
                    </optgroup>
                    <optgroup label="9% Kirchensteuer">
                      <option value="BE">🇩🇪 Berlin (9%)</option>
                      <option value="BB">🇩🇪 Brandenburg (9%)</option>
                      <option value="HB">🇩🇪 Bremen (9%)</option>
                      <option value="HH">🇩🇪 Hamburg (9%)</option>
                      <option value="HE">🇩🇪 Hessen (9%)</option>
                      <option value="MV">🇩🇪 Mecklenburg-Vorpommern (9%)</option>
                      <option value="NI">🇩🇪 Niedersachsen (9%)</option>
                      <option value="NW">🇩🇪 Nordrhein-Westfalen (9%)</option>
                      <option value="RP">🇩🇪 Rheinland-Pfalz (9%)</option>
                      <option value="SL">🇩🇪 Saarland (9%)</option>
                      <option value="SN">🇩🇪 Sachsen (9%)</option>
                      <option value="ST">🇩🇪 Sachsen-Anhalt (9%)</option>
                      <option value="SH">🇩🇪 Schleswig-Holstein (9%)</option>
                      <option value="TH">🇩🇪 Thüringen (9%)</option>
                    </optgroup>
                  </select>
                  {countryInfo?.church_tax && (
                    <p className="text-xs text-purple-600 mt-2">
                      Die Kirchensteuer beträgt 8% (Baden-Württemberg, Bayern) oder 9% (andere Bundesländer) der Einkommensteuer.
                    </p>
                  )}
                  {!countryInfo?.bundesland && !countryInfo?.church_tax && (
                    <p className="text-xs text-gray-500 mt-1">
                      Wählen Sie zuerst Ihr Bundesland, um die Kirchensteuer aktivieren zu können.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info for non-DE users */}
        {(!user?.country || user?.country === 'OTHER') && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="tax-settings-info-banner">
            <div className="flex items-start">
              <span className="text-2xl mr-3">ℹ️</span>
              <div>
                <h4 className="font-medium text-blue-900 mb-2">Steuereinstellungen</h4>
                <p className="text-sm text-blue-800">
                  Erweiterte Steuereinstellungen sind nur für Deutschland, Österreich und die Schweiz verfügbar.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// New component for Country Edit Button
function CountryEditButton() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [countryChangeDialogOpen, setCountryChangeDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(user?.country || 'DE');
  const [saving, setSaving] = useState(false);

  const countries = [
    { code: 'DE', name: 'Deutschland', flag: '🇩🇪' },
    { code: 'AT', name: 'Österreich', flag: '🇦🇹' },
    { code: 'CH', name: 'Schweiz', flag: '🇨🇭' }
  ];

  const handleCountryChange = async () => {
    if (selectedCountry === user?.country) {
      setCountryChangeDialogOpen(false);
      return;
    }

    setSaving(true);
    try {
      await api.put('/country/update', null, { 
        params: { country_code: selectedCountry }
      });
      
      // Update user context directly instead of page reload
      await refreshUser();
      
      toast.success('Steuerland aktualisiert');
      setCountryChangeDialogOpen(false);
    } catch (error) {
      console.error('Error updating country:', error);
      toast.error('Fehler beim Aktualisieren des Steuerlandes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setCountryChangeDialogOpen(true)}
        className="text-blue-600 border-blue-200 hover:bg-blue-50"
      >
        Edit
      </Button>

      <AlertDialog open={countryChangeDialogOpen} onOpenChange={setCountryChangeDialogOpen}>
        <AlertDialogContent className="pointer-events-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Steuerland ändern</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-start">
                  <span className="text-yellow-600 mr-2">⚠️</span>
                  <div>
                    <p className="font-semibold text-yellow-800 mb-2">Wichtiger Hinweis</p>
                    <p className="text-sm text-yellow-700">
                      Die Änderung des Steuerlandes wirkt sich auf alle zukünftigen Steuerberechnungen aus. 
                      Bereits erstellte Rechnungen bleiben unverändert.
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Neues Steuerland auswählen:
                </label>
                <div className="space-y-2">
                  {countries.map((country) => (
                    <div key={country.code} className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id={`country-${country.code}`}
                        name="country"
                        value={country.code}
                        checked={selectedCountry === country.code}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="h-4 w-4 text-blue-600 pointer-events-auto"
                      />
                      <label htmlFor={`country-${country.code}`} className="flex items-center space-x-2 pointer-events-auto cursor-pointer">
                        <span>{country.flag}</span>
                        <span>{country.name}</span>
                        {country.code === user?.country && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Aktuell
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedCountry(user?.country || 'DE')}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCountryChange}
              disabled={saving || selectedCountry === user?.country}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Speichert...' : 'Steuerland ändern'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// New component for Country Information with edit capability
function CountryInfo() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [countryInfo, setCountryInfo] = useState(null);
  const [originalCountryInfo, setOriginalCountryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCanton, setSelectedCanton] = useState('');
  const [saving, setSaving] = useState(false);
  const [countryChangeDialogOpen, setCountryChangeDialogOpen] = useState(false);
  const [pendingCountry, setPendingCountry] = useState('');

  const countries = [
    { code: 'DE', name: t('country.germany', 'Deutschland'), flag: '🇩🇪' },
    { code: 'AT', name: t('country.austria', 'Österreich'), flag: '🇦🇹' },
    { code: 'CH', name: t('country.switzerland', 'Schweiz'), flag: '🇨🇭' },
    { code: 'OTHER', name: t('country.other', 'Anderes Land'), flag: '🌍' }
  ];

  useEffect(() => {
    const fetchCountryInfo = async () => {
      try {
        const response = await api.get('/country/info');
        setCountryInfo(response.data);
        setOriginalCountryInfo(JSON.parse(JSON.stringify(response.data))); // Deep copy
        setSelectedCountry(response.data.country_code);
        setSelectedCanton(response.data.canton || '');
      } catch (error) {
        console.error("Error fetching country info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountryInfo();
  }, []);

  const handleSave = async () => {
    // Validation for CH: canton must be selected
    if (selectedCountry === 'CH' && !selectedCanton) {
      toast.error(t('settings.cantonRequired', 'Bitte wählen Sie einen Kanton aus'));
      return;
    }
    
    setSaving(true);
    try {
      const params = { country_code: selectedCountry };
      if (selectedCountry === 'CH' && selectedCanton) {
        params.canton = selectedCanton;
      }
      if (selectedCountry === 'AT') {
        params.svs_opt_out = countryInfo?.svs_opt_out || false;
      }
      
      await api.put('/country/update', null, { params });
      
      // Refresh country info
      const response = await api.get('/country/info');
      setCountryInfo(response.data);
      setOriginalCountryInfo(JSON.parse(JSON.stringify(response.data))); // Update original after save
      setSelectedCanton(response.data.canton || '');
      setEditing(false);
      toast.success(t('settings.countryUpdated', 'Steueransässigkeit aktualisiert'));
    } catch (error) {
      console.error("Error updating country:", error);
      toast.error(t('settings.countryUpdateError', 'Fehler beim Aktualisieren'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Restore original values
    if (originalCountryInfo) {
      setCountryInfo(JSON.parse(JSON.stringify(originalCountryInfo)));
      setSelectedCountry(originalCountryInfo.country_code);
      setSelectedCanton(originalCountryInfo.canton || '');
    }
    setEditing(false);
  };

  if (loading) {
    return null;
  }

  if (!countryInfo) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{t("settings.countryInfo", "Länderinformationen")}</CardTitle>
            <CardDescription>{t("settings.countryInfoDesc", "Zusätzliche länderspezifische Einstellungen")}</CardDescription>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              data-testid="country-info-edit-button"
            >
              {t("settings.editCountry", "Edit")}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {editing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("settings.selectTaxCountry", "Land der Steuerpflicht")}
                </label>
                <div className="space-y-2">
                  {countries.map(country => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => {
                        // If user tries to change country, show warning dialog
                        if (country.code !== countryInfo.country_code) {
                          setPendingCountry(country.code);
                          setCountryChangeDialogOpen(true);
                        } else {
                          setSelectedCountry(country.code);
                        }
                      }}
                      data-testid={`country-select-${country.code.toLowerCase()}`}
                      className={`w-full flex items-center justify-between p-3 border-2 rounded-xl transition-all ${
                        selectedCountry === country.code
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{country.flag}</span>
                        <span className="font-medium text-gray-900">{country.name}</span>
                      </div>
                      {selectedCountry === country.code && (
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Canton selection for Switzerland */}
              {selectedCountry === 'CH' && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="ch-feature-stub-warning">
                    <span className="text-base mt-0.5">🚧</span>
                    <div className="text-sm text-amber-900">
                      <strong>Schweiz — eingeschränkter Funktionsumfang.</strong>{' '}
                      Stammdaten, Belege und Pre-Check sind funktionsfähig. MWST-Voranmeldung und SB-Export folgen 2026.
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("settings.selectCanton", "Select Your Canton")}
                    </label>
                    <select
                      value={selectedCanton || ''}
                      onChange={(e) => setSelectedCanton(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="country-canton-select"
                    >
                      <option value="">{t("settings.selectCantonPlaceholder", "Please select...")}</option>
                      <option value="ZH">🇨🇭 Zürich</option>
                      <option value="ZG">🇨🇭 Zug ({t('settings.lowestTaxes', 'Lowest taxes')})</option>
                      <option value="BS">🇨🇭 Basel-Stadt</option>
                      <option value="BL">🇨🇭 Basel-Landschaft</option>
                      <option value="BE">🇨🇭 Bern</option>
                      <option value="GE">🇨🇭 Genf ({t('settings.highestTaxes', 'Highest taxes')})</option>
                      <option value="VD">🇨🇭 Waadt</option>
                      <option value="LU">🇨🇭 Luzern</option>
                      <option value="SG">🇨🇭 St. Gallen</option>
                      <option value="AG">🇨🇭 Aargau</option>
                      <option value="TI">🇨🇭 Tessin</option>
                      <option value="GR">🇨🇭 Graubünden</option>
                    </select>
                    <p className="text-xs text-gray-600 mt-2" data-testid="country-canton-note">
                      {t("settings.cantonNote", "Your canton is required for tax calculation. Tax rates vary significantly between cantons.")}
                    </p>
                  </div>
                </>
              )}
              
              {/* SVS Opt-Out settings for Austria */}
              {selectedCountry === 'AT' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="mr-2">🇦🇹</span>
                      {t("settings.svsOptOut", "SVS Opt-Out (Befreiung)")}
                    </label>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${countryInfo?.svs_opt_out ? 'text-red-600' : 'text-gray-500'}`}>
                        {countryInfo?.svs_opt_out ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      {!countryInfo?.svs_opt_out ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              type="button"
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                            >
                              Aktivieren
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-red-600 flex items-center gap-2">
                                <span className="text-2xl">⚠️</span>
                                SVS Opt-Out Bestätigung erforderlich
                              </AlertDialogTitle>
                              <AlertDialogDescription className="space-y-3 text-left">
                                <p className="font-semibold text-gray-900">
                                  Sie sind dabei, sich von der SVS-Pflichtversicherung zu befreien.
                                </p>
                                <div className="bg-red-100 border border-red-300 p-3 rounded">
                                  <p className="font-bold text-red-900 mb-2">⚠️ Kritische Konsequenzen:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm text-red-900">
                                    <li><strong>Kein Krankenversicherungsschutz</strong></li>
                                    <li><strong>Keine Pensionsversicherung</strong></li>
                                    <li><strong>Keine Unfallversicherung</strong></li>
                                    <li>Sie zahlen keine SVS-Beiträge</li>
                                  </ul>
                                </div>
                                <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                                  <p className="font-semibold text-gray-900 mb-2">Voraussetzungen für Opt-Out:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm">
                                    <li>Jahresgewinn ≤ 6.613,20 €</li>
                                    <li>Jahresumsatz ≤ 55.000 €</li>
                                    <li>UND: Neugründer ODER 60+ Jahre alt</li>
                                  </ul>
                                </div>
                                <p className="text-sm font-semibold text-gray-900">
                                  Bestätigen Sie, dass Sie die Konsequenzen verstanden haben und die Voraussetzungen erfüllen?
                                </p>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Nein, abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => setCountryInfo({...countryInfo, svs_opt_out: true})}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Ja, ich verstehe - Opt-Out aktivieren
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCountryInfo({...countryInfo, svs_opt_out: false})}
                          className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded transition-colors"
                        >
                          Deaktivieren
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-700 space-y-2">
                    <p className="font-semibold">
                      {t("settings.svsOptOutTitle", "Befreiung von der SVS-Pflichtversicherung")}
                    </p>
                    <p>
                      {t("settings.svsOptOutExplanation", "You can be exempted from SVS if your annual profit is ≤ €6,613.20 and annual revenue is ≤ €55,000.")}
                    </p>
                    <div className="bg-white p-3 rounded border border-red-300">
                      <p className="font-semibold mb-1">Voraussetzungen:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Jahresgewinn max. 6.613,20 €</li>
                        <li>Jahresumsatz max. 55.000 €</li>
                        <li>UND: Neugründer ODER 60+ Jahre alt</li>
                      </ul>
                    </div>
                    <p className="text-red-700 font-semibold">
                      ⚠️ Achtung: Bei Opt-Out haben Sie keinen SVS-Versicherungsschutz (Kranken-, Pensions-, Unfallversicherung)!
                    </p>
                  </div>
                </div>
              )}
              
              {/* Warning for OTHER country selection */}
              {selectedCountry === 'OTHER' && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-orange-800">
                      <p className="font-semibold mb-1">
                        {t("settings.otherCountryTitle", "Limited Functionality")}
                      </p>
                      <p className="mb-2">
                        {t("settings.otherCountryWarning", "No specific tax calculations available for your country.")}
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>{t("settings.otherCountryLimit1", "Keine automatische Steuervorberechnung")}</li>
                        <li>{t("settings.otherCountryLimit2", "Generische Mehrwertsteuersätze")}</li>
                        <li>{t("settings.otherCountryLimit3", "You must manually enter tax rates in invoices")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Country Change Warning Dialog */}
              <AlertDialog open={countryChangeDialogOpen} onOpenChange={setCountryChangeDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-orange-600 flex items-center gap-2">
                      <span className="text-2xl">⚠️</span>
                      Warnung: Länderwechsel
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-3 text-left">
                      <p className="font-semibold text-gray-900">
                        Ein Wechsel des Landes kann zu Inkonsistenzen führen.
                      </p>
                      <div className="bg-orange-50 border border-orange-300 p-3 rounded">
                        <p className="font-bold text-orange-900 mb-2">Auswirkungen:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-orange-900">
                          <li><strong>Bestehende Rechnungen</strong> behalten ihre ursprüngliche Währung (keine Konvertierung)</li>
                          <li><strong>Neue Rechnungen</strong> werden in der neuen Währung erstellt</li>
                          <li><strong>Steuersätze</strong> werden auf das neue Land angepasst</li>
                          <li><strong>Ausgaben</strong> werden konvertiert, was zu Rundungsfehlern führen kann</li>
                        </ul>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                        <p className="font-semibold text-blue-900 mb-2">💡 Unsere Empfehlung:</p>
                        <p className="text-sm text-blue-800">
                          Wenn Sie dauerhaft in ein anderes Land wechseln, empfehlen wir ein <strong>neues Konto</strong> zu erstellen, um Ihre Buchhaltung sauber zu trennen.
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        Möchten Sie das Land wirklich wechseln?
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel
                      onClick={() => {
                      setPendingCountry('');
                      setCountryChangeDialogOpen(false);
                      }}
                      data-testid="country-change-cancel-button"
                    >
                      Abbrechen
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        setSelectedCountry(pendingCountry);
                        setPendingCountry('');
                        setCountryChangeDialogOpen(false);
                      }}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="country-change-confirm-button"
                    >
                      Ja, Land wechseln
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving || selectedCountry === countryInfo.country_code}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-xl transition-colors"
                  data-testid="country-info-save-button"
                >
                  {saving ? t("common.saving", "Saving...") : t("common.save", "Save")}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 font-medium py-2 px-4 rounded-xl transition-colors"
                  data-testid="country-info-cancel-button"
                >
                  {t("settings.cancel", "Abbrechen")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <span className="font-medium">{t("settings.country")}:</span>
                <span className="text-gray-700" data-testid="country-info-name">{countryInfo.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium">{t("settings.currency")}:</span>
                <span className="text-gray-700" data-testid="country-info-currency">{countryInfo.currency}</span>
              </div>
              <div>
                <span className="font-medium">{t("settings.vatRates")}:</span>
                <ul className="mt-2 space-y-1 ml-4" data-testid="country-info-vat-rates">
                  {countryInfo.vat_rates?.map((rate, index) => (
                    <li key={index} className="text-sm text-gray-600" data-testid={`country-info-vat-rate-${index}`}>
                      {rate.label}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Info box for OTHER country */}
              {countryInfo.country_code === 'OTHER' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">
                        {t("settings.otherCountryInfoTitle", "Hinweis zur Steuerberechnung")}
                      </p>
                      <p>
                        {t("settings.otherCountryInfoText", "Automatic tax calculations are not available for your country. You can use generic VAT rates or enter your own values in invoices.")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-4">
                {t("settings.countryChangeHint", "Du kannst dein Steuerland jederzeit über den 'Edit'-Button oben rechts ändern.")}
              </p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}



export { TaxSettings, CountryEditButton, CountryInfo };
